import * as React from 'react'
import { useRef } from 'react'
import Mark from 'mark.js'

import { useDocumentStore } from '@/features/document'

/** Selectors excluded from mark.js highlighting (e.g. SVG to avoid wrapping text in &lt;mark&gt; inside SVG). */
const MARK_EXCLUDE_SELECTORS = ['svg', 'text']

type TUseMarkHighlightProps = {
  isReady: boolean
  containerRef: React.RefObject<ShadowRoot | HTMLDivElement | null>
  documentId: string
  dependencies?: unknown[]
}

type TUseMarkHighlightResult = {
  markInstanceRef: React.RefObject<Mark | null>
  searchText: string
}

export const useMarkHighlight = (
  props: TUseMarkHighlightProps
): TUseMarkHighlightResult => {
  const { isReady, containerRef, documentId, dependencies } = props

  const {
    searchText,
    isSearchResultsExists,
    focusSearchResult,
    activeDocumentId,
  } = useDocumentStore()

  const isActive = React.useMemo(
    () => activeDocumentId === documentId,
    [activeDocumentId, documentId]
  )

  const markInstanceRef = useRef<Mark | null>(null)

  const handleFocusMark = React.useCallback(() => {
    if (!focusSearchResult) return

    setTimeout(() => {
      const foundMarks = containerRef.current?.querySelectorAll('mark')
      const targetMark = foundMarks?.[focusSearchResult.index]
      if (!targetMark) return

      targetMark.style.backgroundColor = '#FFCC33'
      targetMark.scrollIntoView({ behavior: 'smooth' })
    })
  }, [containerRef, focusSearchResult])

  React.useEffect(() => {
    if (!isReady || !isActive) return

    const root = containerRef?.current
    if (!root) return

    markInstanceRef.current ??= new Mark(root as unknown as HTMLElement)

    setTimeout(() => {
      // Unmark all previous search text (exclude SVG so we don't touch content inside SVG)
      markInstanceRef.current?.unmark({
        exclude: MARK_EXCLUDE_SELECTORS,
        done: () => {
          // Mark new search text (exclude SVG to avoid <mark> inside SVG elements)
          if (!isSearchResultsExists || !focusSearchResult) return

          markInstanceRef.current?.mark(focusSearchResult.keywords, {
            separateWordSearch: false,
            exclude: MARK_EXCLUDE_SELECTORS,
          })

          handleFocusMark()
        },
      })
    }, 300)
  }, [
    containerRef,
    focusSearchResult,
    handleFocusMark,
    isActive,
    isReady,
    isSearchResultsExists,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(dependencies || []),
  ])

  return React.useMemo(
    () => ({ markInstanceRef, searchText }),
    [markInstanceRef, searchText]
  )
}
